import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Search, X, Package } from 'lucide-react';
import BackButton from '../components/BackButton';
import { spacing, fontWeight } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  height: 100vh;
  background: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  padding: 4px;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;

const HeaderTitle = styled.h1`
  font-size: 17px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  flex: 1;
  text-align: center;
  margin-right: 32px;
  letter-spacing: -0.01em;
`;

const SearchBar = styled.div`
  padding: 10px 16px 8px;
  flex-shrink: 0;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 9px 12px;
  gap: 8px;
`;

const SearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 15px;
  color: ${props => props.theme.colors.text};
  font-family: inherit;
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  padding: 0;
  &:hover { color: rgba(255,255,255,0.6); }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 16px 24px;
  animation: ${fadeUp} 0.3s ease both;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 20px 0 6px 4px;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.06);
  overflow: hidden;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 12px;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
`;

const Thumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
`;

const ProductMeta = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-top: 2px;
`;

const Toggles = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const ToggleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

const ToggleLabel = styled.div`
  font-size: 10px;
  color: rgba(255,255,255,0.3);
  font-weight: 500;
  text-align: center;
`;

const Toggle = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease;
  background: ${props => props.$on ? props.$color || '#f97316' : 'rgba(255,255,255,0.12)'};
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    top: 2px;
    left: ${props => props.$on ? '22px' : '2px'};
    transition: left 0.2s ease;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: rgba(255,255,255,0.3);
  gap: 12px;
  font-size: 15px;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255,255,255,0.3);
  font-size: 15px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProductStatusScreen() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const client = getKmfcClient();
      if (!client) return;

      const { data, error } = await client
        .from('products')
        .select('*, categories(name, position)')
        .order('categories(position)', { ascending: true })
        .order('sort_index', { ascending: true });

      if (error) throw error;

      const sorted = (data || []).sort((a, b) => {
        const pa = a.categories?.position ?? 999;
        const pb = b.categories?.position ?? 999;
        if (pa !== pb) return pa - pb;
        return (a.sort_index ?? 999) - (b.sort_index ?? 999);
      });

      setProducts(sorted);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (productId, field, current) => {
    const newVal = !current;
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: newVal } : p));

    try {
      const client = getKmfcClient();
      if (!client) return;
      const { error } = await client.from('products').update({ [field]: newVal }).eq('id', productId);
      if (error) throw error;
    } catch (err) {
      // revert on error
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: current } : p));
      console.error('Error updating product:', err);
    }
  };

  const filtered = search.trim()
    ? products.filter(p =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.categories?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  // Group by category
  const grouped = [];
  const seen = new Map();
  for (const p of filtered) {
    const cat = p.categories?.name || 'Overig';
    if (!seen.has(cat)) { seen.set(cat, []); grouped.push({ cat, items: seen.get(cat) }); }
    seen.get(cat).push(p);
  }

  return (
    <Container>


      <SearchBar>
        <SearchWrap>
          <Search size={16} color="rgba(255,255,255,0.3)" />
          <SearchInput
            placeholder="Zoek product of categorie..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <ClearBtn onClick={() => setSearch('')}>
              <X size={16} />
            </ClearBtn>
          )}
        </SearchWrap>
      </SearchBar>

      <Content>
        {loading ? (
          <LoadingState>Producten laden...</LoadingState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <Package size={40} />
            {search ? 'Geen producten gevonden' : 'Geen producten beschikbaar'}
          </EmptyState>
        ) : (
          grouped.map(({ cat, items }) => (
            <div key={cat}>
              <SectionLabel>{cat}</SectionLabel>
              <Card>
                {items.map(product => (
                  <ProductRow key={product.id}>
                    <Thumb>
                      {product.image_url
                        ? <img src={product.image_url} alt={product.name} />
                        : <Package size={18} color="rgba(255,255,255,0.2)" />
                      }
                    </Thumb>

                    <ProductInfo>
                      <ProductName>{product.name}</ProductName>
                      <ProductMeta>€{(product.price_cents / 100).toFixed(2)}</ProductMeta>
                    </ProductInfo>

                    <Toggles>
                      <ToggleWrap>
                        <Toggle
                          $on={product.is_active}
                          $color="#f97316"
                          onClick={() => toggle(product.id, 'is_active', product.is_active)}
                        />
                        <ToggleLabel>Actief</ToggleLabel>
                      </ToggleWrap>

                      <ToggleWrap>
                        <Toggle
                          $on={!product.is_sold_out}
                          $color="#22c55e"
                          onClick={() => toggle(product.id, 'is_sold_out', product.is_sold_out)}
                        />
                        <ToggleLabel>Voorraad</ToggleLabel>
                      </ToggleWrap>
                    </Toggles>
                  </ProductRow>
                ))}
              </Card>
            </div>
          ))
        )}
      </Content>
    </Container>
  );
}
